import { EChartsType } from 'echarts';
import type { ILegendItemRect } from '../_config/interface';

type ZRenderElement = any;
type ZRenderInstance = any;

/**
 * 从 ZRender 目标元素向上遍历，识别被 hover 的 legend item 名称。
 *
 * ECharts legend item 结构：LegendView(root) > scrollBox > itemGroup > { hitRect, icon, label }
 * - hitRect 是最顶层拦截元素（无 style.text），itemGroup 上有 __legendDataIndex
 * - 同时兼容 label 直接命中（有 style.text）以及 __ecComponentInfo 方式
 */
export function getLegendItemName(target: ZRenderElement, chartInstance: EChartsType): string | null {
  let el: ZRenderElement = target;
  let foundText: string | null = null;
  while (el) {
    if (el.__ecComponentInfo?.mainType === 'legend') {
      return foundText;
    }
    if (el.__legendDataIndex !== undefined) {
      const opt = (chartInstance as any).getOption?.() as any;
      const legendData: any[] = opt?.legend?.[0]?.data ?? opt?.legend?.data ?? [];
      const item = legendData[el.__legendDataIndex];
      if (item == null) return null;
      return typeof item === 'string' ? item : (item?.name ?? null);
    }
    if (typeof el.style?.text === 'string' && el.style.text) {
      foundText = el.style.text;
    }
    el = el.parent;
  }
  return null;
}

/**
 * 计算 legend item 在页面上的 DOM bounding rect，供调用方锚定 tooltip。
 * 从 target 向上找到带 __legendDataIndex 的 itemGroup，再沿 ZRender 树累加 position。
 */
export function getLegendItemDOMRect(target: ZRenderElement, canvasEl: HTMLElement): ILegendItemRect | undefined {
  let itemGroup: ZRenderElement = target;
  while (itemGroup && itemGroup.__legendDataIndex === undefined && itemGroup.__ecComponentInfo?.mainType !== 'legend') {
    itemGroup = itemGroup.parent;
  }
  if (itemGroup?.__legendDataIndex === undefined) return undefined;

  let gx = 0, gy = 0;
  let el: ZRenderElement = itemGroup;
  while (el) {
    gx += (el.position?.[0] ?? 0);
    gy += (el.position?.[1] ?? 0);
    el = el.parent;
  }
  const localRect = itemGroup.getBoundingRect();
  const canvasRect = canvasEl.getBoundingClientRect();
  return {
    left: canvasRect.left + gx + localRect.x,
    top: canvasRect.top + gy + localRect.y,
    width: localRect.width,
    height: localRect.height,
    right: canvasRect.left + gx + localRect.x + localRect.width,
    bottom: canvasRect.top + gy + localRect.y + localRect.height,
  };
}

/**
 * 获取 ECharts 实例的 ZRender 实例（类型不对外暴露，返回 any）。
 */
export function getZRender(chartInstance: EChartsType): ZRenderInstance | null {
  return (chartInstance as any).getZr?.() ?? null;
}
